<template>
  <ProcedurePage
    :id="$route.params.id"
    :procedure="procedure"
    :result="result"
    :actions-info="actionsInfo"
    :load-func="load"
    :status="procedure.status"
    :types="types"
    :isClosed="procedure.isClosed"
    :show-cancel-details="true"
    @update:procedureGoodName="procedure.goodName = $event"
    @update:procedureType="procedure.type = $event"
  >
    <ContactPerson
      v-if="!isHiddenContractPerson"
      :organization="procedure.orgFullName"
      :contact="procedure.contactPerson"
      :view="true"
    />

    <WinnersInfo
      :winners="winners"
      :withPositions="procedure.withPositions"
      :procedure-step-code="procedure.status.code"
      :procedure="procedure"
    />
  </ProcedurePage>
</template>

<script setup lang="ts">
import api from '@/api';
import { toast } from '@/lib/toast';
import { computed, reactive, ref } from 'vue';
import ProcedurePage from "@/components/ProcedurePage.vue";
import ContactPerson from '@/components/procedure/ContactPerson.vue';
import WinnersInfo from '@/components/procedure/WinnersInfo.vue';
import Config from '@/config';

const loading = ref<Boolean>();

interface Procedure {
  id?: number
  type?: string
  goodName?: string
  registryNumber?: string
  status: {
    name: string
    code: string
  }
  startMaxPrice?: string
  orgFullName?: string,
  contactPerson: {
    userFullName: string,
    userPhone: string,
    userEmail: string
  },
  withPositions: boolean,
  isWithoutStartMaxPrice?: boolean,
  isNds?: boolean,
  isClosed: boolean,
}

const procedure = reactive<Procedure>({
  id: null,
  type: '',
  goodName: '',
  registryNumber: '',
  status: { name: '', code: '' },
  startMaxPrice: '',
  orgFullName: '',
  contactPerson: {
    userFullName: '',
    userPhone: '',
    userEmail: '',
  },
  withPositions: false,
  isWithoutStartMaxPrice: false,
  isNds: false,
  isClosed: false,
});

interface Access {
  canViewCompletedProcedure: boolean;
}

const access = reactive<Access>({
  canViewCompletedProcedure: false,
});

const procedureActions = reactive({
  primaryAction: [],
  secondaryActions: [],
});

const types = ref<{
  title: string
  value: string
}[]>([]);

const winners = reactive([]);

const result = reactive({
  isWinner: false,
  isCurrentContragentWinner: false,
});

const isHiddenContractPerson = Config.getConfig('HIDDEN_CONTACT_PERSON');

const actionsInfo = computed<object>(
  () => ({
    p: {
      id: procedure.id,
      registryNumber: procedure.registryNumber
    },
    access,
    procedureActions,
  })
);

const load = async (procedureId) => {
  loading.value = true;

  try {
    const resp = await api.get(`/procedures/${procedureId}/completed`);

    Object.assign(procedure, resp.procedure);
    Object.assign(procedureActions, resp.procedureActions);
    Object.assign(access, resp.access);
    Object.assign(winners, resp.winners);
    Object.assign(result, resp.result);

    types.value = resp.types.map((elem) => ({
      title: elem.name,
      value: elem.code,
    }));

  } catch (e) {
    toast.error(e.message);
  } finally {
    loading.value = false;
  }
};
</script>
